import { PayloadAction } from '@reduxjs/toolkit';
import { trackException } from 'app-insights/appInsightsTracking';
import { ExceptionType } from 'app/enums/Common';
import { all, call, put, select } from 'redux-saga/effects';
import { placementModsAction } from './slice';
import {
  postBillRate,
  postBillingShiftDifferential,
  postBonus,
  postEmiLmo,
  postEndDate,
  postFurniture,
  postHousing,
  postInsurance,
  postLodging,
  postMinReqHrs,
  postOGPLoan,
  postPTOMod,
  postPayRate,
  postPayrollShiftDifferential,
  postPreference,
  postReimbursement,
  postShift,
  postStartDate,
  postTimeOff,
  postTravelAllowance,
} from '@AMIEWEB/Placement/PlacementDetails/PlacementTabPanel/ModificationsTab/CommonComponents/UpdateHelper';
import {
  addArrivingTravelModDetails,
  addBillRateModDetails,
  addBillingShiftDifferentialModDetails,
  addBonusModDetails,
  addEmiLmoModDetails,
  addEndDateModDetails,
  addFurnitureModDetails,
  addHousingModDetails,
  addInsuranceModDetails,
  addLodgingModDetails,
  addMinReqHoursModDetails,
  addOGPModDetails,
  addPTOModDetails,
  addPayRateModDetails,
  addPayrollShiftDifferentialModDetails,
  addPreferenceModDetails,
  addReimbursementModDetails,
  addShiftModDetails,
  addStartDateModDetails,
  addTimeOffModDetails,
  deleteMods,
  batchDeleteMods,
  batchUpdateMods,
  updateBillRateModDetails,
  updateBillingShiftDifferentialModDetails,
  updateBonusModDetails,
  updateEmiLmoModDetails,
  updateEndDateModDetails,
  updateFurnitureModDetails,
  updateHousingModDetails,
  updateInsuranceModDetails,
  updateLodgingModDetails,
  updateMinReqHoursModDetails,
  updateOGPModDetails,
  updatePTOModDetails,
  updatePayRateModDetails,
  updatePayrollShiftDifferentialModDetails,
  updatePreferenceModDetails,
  updateReimbursementModDetails,
  updateShiftModDetails,
  updateStartDateModDetails,
  updateTimeOffModDetails,
  updateTravelAllowanceModDetails,
  voidMods,
} from './update.saga';
import { selectUser } from 'oidc/user.selectors';
import { MODIFICATIONS_GRID_PAGE_SIZE, PTOMod, modsEnums } from 'app/models/Placement/PlacementMods';
import { requestBookingApprovedCheckboxSpecs } from './request.saga';
import { placementSummaryActions } from '../placement-summary/slice';
import { selectPlacementDetails } from '../placement-details/selectors';
import { IUpdateModStatusRequest, IDeleteModStatusRequest } from './business-action';
import { requestBookingApprovedRequirements } from '../placement-summary/common-saga';
import { GridType } from 'app/components/Common/types';

// Helper function to refresh both grids. Sorting is handled client-side by the grids.
function* refreshModificationGrids(placementId: number) {
  yield all([
    // Refresh pending mods
    put(placementModsAction.getModificationAction({
      pageNumber: 1,
      pageSize: MODIFICATIONS_GRID_PAGE_SIZE,
      placementId: Number(placementId),
      offSet: 0,
      gridType: GridType.modification,
      sortColumn: null,
      sortOrder: null,
    })),
    // Refresh history mods
    put(placementModsAction.getModificationAction({
      pageNumber: 1,
      pageSize: MODIFICATIONS_GRID_PAGE_SIZE,
      placementId: Number(placementId),
      offSet: 0,
      gridType: null,
      sortColumn: null,
      sortOrder: null,
    })),
  ]);
}

export function* setEditPreferenceRows(action: PayloadAction<any>) {
  try {
    const { adults, children, pets } = action.payload;
    yield put(placementModsAction.setEditAdultRows(adults || []));
    yield put(placementModsAction.setEditChildrenRows(children || []));
    yield put(placementModsAction.setEditPetsRows(pets || []));
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.CommonSAGAError,
        functionName: 'setEditPreferenceRows',
        area: 'src/store/redux-store/placement-mods/business-saga.ts',
      },
    });
  }
}

export function* createPlacementModDetails(
  action: PayloadAction<{
    selectedRow;
    placementId;
    formData;
    selectedButton;
    modDetails;
    timeOffEditRequests;
    furnitureEditRequests;
    utilityEditRequests;
    payrollShiftEditRequests;
    reimbursementCategoryAndFrequency;
    preferenceModEditRequests;
    billingShiftEditRequests;
    gwwExceptionsTypes;
    pricingReasons;
  }>,
) {
  try {
    let result;
    const {
      selectedRow,
      placementId,
      formData,
      selectedButton,
      modDetails,
      timeOffEditRequests,
      furnitureEditRequests,
      utilityEditRequests,
      payrollShiftEditRequests,
      reimbursementCategoryAndFrequency,
      preferenceModEditRequests,
      billingShiftEditRequests,
      gwwExceptionsTypes,
      pricingReasons,
    } = action.payload;
    const selectedRowType = selectedRow?.type?.replaceAll(/['.\s]/g, '');
    const {
      userInfo: { employeeId },
    } = yield select(selectUser);
    if (selectedRowType === 'MinReqHours') {
      result = yield call(addMinReqHoursModDetails, {
        payload: postMinReqHrs(placementId, formData, selectedButton, employeeId, modDetails),
        type: placementModsAction.addMinReqHours.type,
      });
    } else if (selectedRowType === 'PayRate') {
      result = yield call(addPayRateModDetails, {
        payload: postPayRate(placementId, formData, selectedButton, employeeId, modDetails, gwwExceptionsTypes),
        type: placementModsAction.addPayRate.type,
      });
    } else if (selectedRowType === 'LodgingPerDiemAdjustment') {
      result = yield call(addLodgingModDetails, {
        payload: postLodging(placementId, formData, selectedButton, employeeId, modDetails),
        type: placementModsAction.addLodgingModDetails.type,
      });
    } else if (selectedRowType === 'EndDate') {
      result = yield call(addEndDateModDetails, {
        payload: postEndDate(placementId, formData, selectedButton, employeeId, modDetails),
        type: placementModsAction.addEndDateModDetails.type,
      });
    } else if (selectedRowType === 'StartDate') {
      result = yield call(addStartDateModDetails, {
        payload: postStartDate(placementId, formData, selectedButton, employeeId, modDetails),
        type: placementModsAction.addStartDateModDetails.type,
      });
    } else if (selectedRowType === 'ArrivingTravel') {
      result = yield call(addArrivingTravelModDetails, {
        payload: postTravelAllowance(
          placementId,
          formData,
          selectedButton,
          employeeId,
          modDetails,
          modsEnums.ArrivingTravel,
          selectedRow?.type,
        ),
        type: placementModsAction.addTravelAllowanceModDetailsAction.type,
      });
    } else if (selectedRowType === 'EndingTravel') {
      result = yield call(addArrivingTravelModDetails, {
        payload: postTravelAllowance(
          placementId,
          formData,
          selectedButton,
          employeeId,
          modDetails,
          modsEnums.EndingTravel,
          selectedRow?.type,
        ),
        type: placementModsAction.addTravelAllowanceModDetailsAction.type,
      });
    } else if (selectedRowType === 'InterimTravel') {
      result = yield call(addArrivingTravelModDetails, {
        payload: postTravelAllowance(
          placementId,
          formData,
          selectedButton,
          employeeId,
          modDetails,
          modsEnums.InterimTravel,
          selectedRow?.type,
        ),
        type: placementModsAction.addTravelAllowanceModDetailsAction.type,
      });
    } else if (selectedRowType === 'Shift') {
      result = yield call(addShiftModDetails, {
        payload: postShift(placementId, formData, selectedButton, employeeId, modDetails),
        type: placementModsAction.addShiftModDetails.type,
      });
    } else if (selectedRowType === 'EMI/LMO') {
      result = yield call(addEmiLmoModDetails, {
        payload: postEmiLmo(placementId, formData, selectedButton, employeeId, modDetails),
        type: placementModsAction.addEmiLmoModDetailsAction.type,
      });
    } else if (selectedRowType === 'Bonus') {
      result = yield call(addBonusModDetails, {
        payload: postBonus(placementId, formData, selectedButton, employeeId, modDetails),
        type: placementModsAction.addBonusModDetailsAction.type,
      });
    } else if (selectedRowType === 'Insurance') {
      result = yield call(addInsuranceModDetails, {
        payload: postInsurance(placementId, formData, selectedButton, employeeId, modDetails),
        type: placementModsAction.addInsuranceModDetails.type,
      });
    } else if (selectedRowType === 'Reimbursement') {
      result = yield call(addReimbursementModDetails, {
        payload: postReimbursement(
          placementId,
          formData,
          selectedButton,
          employeeId,
          modDetails,
          reimbursementCategoryAndFrequency,
        ),
        type: placementModsAction.addReimbursementModDetails.type,
      });
    } else if (selectedRowType === 'Timeoff') {
      result = yield call(addTimeOffModDetails, {
        payload: postTimeOff(placementId, timeOffEditRequests, selectedButton, employeeId, modDetails),
        type: placementModsAction.addTimeOffModDetails.type,
      });
    } else if (selectedRowType === 'Furniture') {
      result = yield call(addFurnitureModDetails, {
        payload: postFurniture(
          placementId,
          formData,
          selectedButton,
          employeeId,
          modDetails,
          furnitureEditRequests,
          utilityEditRequests,
        ),
        type: placementModsAction.addFurnitureModDetails.type,
      });
    } else if (selectedRowType === 'BillRate') {
      result = yield call(addBillRateModDetails, {
        payload: postBillRate(
          placementId,
          formData,
          selectedButton,
          employeeId,
          modDetails,
          gwwExceptionsTypes,
          pricingReasons,
        ),
        type: placementModsAction.addBillRateModDetails.type,
      });
    } else if (selectedRowType === 'PayrollShiftDifferential') {
      result = yield call(addPayrollShiftDifferentialModDetails, {
        payload: postPayrollShiftDifferential(
          placementId,
          formData,
          payrollShiftEditRequests,
          selectedButton,
          employeeId,
          modDetails,
        ),
        type: placementModsAction.addPayrollShiftDifferentialModDetails.type,
      });
    } else if (selectedRowType === 'Preferences') {
      result = yield call(addPreferenceModDetails, {
        payload: postPreference(placementId, preferenceModEditRequests, selectedButton, employeeId, modDetails),
        type: placementModsAction.addPreferenceModDetails.type,
      });
    } else if (selectedRowType === PTOMod) {
      result = yield call(addPTOModDetails, {
        payload: postPTOMod(placementId, formData, selectedButton, employeeId, modDetails),
        type: placementModsAction.addPTOModDetails.type,
      });
    } else if (selectedRowType === 'OGPIntlLoan') {
      result = yield call(addOGPModDetails, {
        payload: postOGPLoan(placementId, formData, selectedButton, employeeId, modDetails),
        type: placementModsAction.addOGPModDetails.type,
      });
    } else if (selectedRowType === 'BillingShiftDifferential') {
      result = yield call(addBillingShiftDifferentialModDetails, {
        payload: postBillingShiftDifferential(
          placementId,
          formData,
          billingShiftEditRequests,
          selectedButton,
          employeeId,
          modDetails,
        ),
        type: placementModsAction.addBillingShiftDifferentialModDetails.type,
      });
    } else if (selectedRowType === 'Housing') {
      result = yield call(addHousingModDetails, {
        payload: postHousing(placementId, formData, selectedButton, employeeId, modDetails),
        type: placementModsAction.addHousingModDetailsAction.type,
      });
    }
    if (result) {
      yield call(refreshBookingApprovedStatusSpecs);
      yield put(placementModsAction.getPlacementModsList({ placementId: placementId }));
      yield put(placementModsAction.getRefreshPlacementModsList({ placementId: placementId }));
      yield call(refreshModificationGrids, placementId);
    }
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.CommonSAGAError,
        functionName: 'createPlacementModDetails',
        area: 'src/store/redux-store/placement-mods/business-saga.ts',
      },
    });
  }
}

export function* editPlacementModDetails(
  action: PayloadAction<{
    selectedRow;
    placementId;
    formData;
    selectedButton;
    timeOffEditRequests;
    modDetails;
    furnitureEditRequests;
    utilityEditRequests;
    payrollShiftEditRequests;
    reimbursementCategoryAndFrequency;
    preferenceModEditRequests;
    billingShiftEditRequests;
    gwwExceptionsTypes;
    pricingReasons;
  }>,
) {
  try {
    let result;
    const {
      selectedRow,
      placementId,
      formData,
      selectedButton,
      modDetails,
      timeOffEditRequests,
      furnitureEditRequests,
      utilityEditRequests,
      payrollShiftEditRequests,
      reimbursementCategoryAndFrequency,
      preferenceModEditRequests,
      billingShiftEditRequests,
      gwwExceptionsTypes,
      pricingReasons,
    } = action.payload;
    const selectedRowType = selectedRow?.type?.replaceAll(/['.\s]/g, '');
    const {
      userInfo: { employeeId },
    } = yield select(selectUser);
    if (selectedRowType === 'MinReqHours') {
      result = yield call(updateMinReqHoursModDetails, {
        payload: postMinReqHrs(placementId, formData, selectedButton, employeeId, modDetails),
        type: placementModsAction.updateMinReqHours.type,
      });
    } else if (selectedRowType === 'PayRate') {
      result = yield call(updatePayRateModDetails, {
        payload: postPayRate(placementId, formData, selectedButton, employeeId, modDetails, gwwExceptionsTypes),
        type: placementModsAction.updatePayRate.type,
      });
    } else if (selectedRowType === 'LodgingPerDiemAdjustment') {
      result = yield call(updateLodgingModDetails, {
        payload: postLodging(placementId, formData, selectedButton, employeeId, modDetails),
        type: placementModsAction.updateLodgingModDetails.type,
      });
    } else if (selectedRowType === 'EndDate') {
      result = yield call(updateEndDateModDetails, {
        payload: postEndDate(placementId, formData, selectedButton, employeeId, modDetails),
        type: placementModsAction.updateEndDateModDetails.type,
      });
    } else if (selectedRowType === 'StartDate') {
      result = yield call(updateStartDateModDetails, {
        payload: postStartDate(placementId, formData, selectedButton, employeeId, modDetails),
        type: placementModsAction.updateStartDateModDetails.type,
      });
    } else if (selectedRowType === 'ArrivingTravel') {
      result = yield call(updateTravelAllowanceModDetails, {
        payload: postTravelAllowance(
          placementId,
          formData,
          selectedButton,
          employeeId,
          modDetails,
          modsEnums.ArrivingTravel,
          selectedRow?.type,
        ),
        type: placementModsAction.updateTravelAllowanceModDetailsAction.type,
      });
    } else if (selectedRowType === 'EndingTravel') {
      result = yield call(updateTravelAllowanceModDetails, {
        payload: postTravelAllowance(
          placementId,
          formData,
          selectedButton,
          employeeId,
          modDetails,
          modsEnums.EndingTravel,
          selectedRow?.type,
        ),
        type: placementModsAction.updateTravelAllowanceModDetailsAction.type,
      });
    } else if (selectedRowType === 'InterimTravel') {
      result = yield call(updateTravelAllowanceModDetails, {
        payload: postTravelAllowance(
          placementId,
          formData,
          selectedButton,
          employeeId,
          modDetails,
          modsEnums.InterimTravel,
          selectedRow?.type,
        ),
        type: placementModsAction.updateTravelAllowanceModDetailsAction.type,
      });
    } else if (selectedRowType === 'Shift') {
      result = yield call(updateShiftModDetails, {
        payload: postShift(placementId, formData, selectedButton, employeeId, modDetails),
        type: placementModsAction.updateShiftModDetails.type,
      });
    } else if (selectedRowType === 'EMI/LMO') {
      result = yield call(updateEmiLmoModDetails, {
        payload: postEmiLmo(placementId, formData, selectedButton, employeeId, modDetails),
        type: placementModsAction.updateEmiLmoModDetailsAction.type,
      });
    } else if (selectedRowType === 'Bonus') {
      result = yield call(updateBonusModDetails, {
        payload: postBonus(placementId, formData, selectedButton, employeeId, modDetails),
        type: placementModsAction.updateBonusModDetailsAction.type,
      });
    } else if (selectedRowType === 'Insurance') {
      result = yield call(updateInsuranceModDetails, {
        payload: postInsurance(placementId, formData, selectedButton, employeeId, modDetails),
        type: placementModsAction.updateInsuranceModDetails.type,
      });
    } else if (selectedRowType === 'Reimbursement') {
      result = yield call(updateReimbursementModDetails, {
        payload: postReimbursement(
          placementId,
          formData,
          selectedButton,
          employeeId,
          modDetails,
          reimbursementCategoryAndFrequency,
        ),
        type: placementModsAction.updateReimbursementModDetails.type,
      });
    } else if (selectedRowType === 'Timeoff') {
      result = yield call(updateTimeOffModDetails, {
        payload: postTimeOff(placementId, timeOffEditRequests, selectedButton, employeeId, modDetails),
        type: placementModsAction.updateTimeOffModDetails.type,
      });
    } else if (selectedRowType === 'Furniture') {
      result = yield call(updateFurnitureModDetails, {
        payload: postFurniture(
          placementId,
          formData,
          selectedButton,
          employeeId,
          modDetails,
          furnitureEditRequests,
          utilityEditRequests,
        ),
        type: placementModsAction.updateFurnitureModDetails.type,
      });
    } else if (selectedRowType === 'BillRate') {
      result = yield call(updateBillRateModDetails, {
        payload: postBillRate(
          placementId,
          formData,
          selectedButton,
          employeeId,
          modDetails,
          gwwExceptionsTypes,
          pricingReasons,
        ),
        type: placementModsAction.updateBillRateModDetails.type,
      });
    } else if (selectedRowType === 'PayrollShiftDifferential') {
      result = yield call(updatePayrollShiftDifferentialModDetails, {
        payload: postPayrollShiftDifferential(
          placementId,
          formData,
          payrollShiftEditRequests,
          selectedButton,
          employeeId,
          modDetails,
        ),
        type: placementModsAction.updatePayrollShiftDifferentialModDetails.type,
      });
    } else if (selectedRowType === 'Preferences') {
      result = yield call(updatePreferenceModDetails, {
        payload: postPreference(placementId, preferenceModEditRequests, selectedButton, employeeId, modDetails),
        type: placementModsAction.updatePreferenceModDetails.type,
      });
    } else if (selectedRowType === PTOMod) {
      result = yield call(updatePTOModDetails, {
        payload: postPTOMod(placementId, formData, selectedButton, employeeId, modDetails),
        type: placementModsAction.updatePTOModDetails.type,
      });
    } else if (selectedRowType === 'OGPIntlLoan') {
      result = yield call(updateOGPModDetails, {
        payload: postOGPLoan(placementId, formData, selectedButton, employeeId, modDetails),
        type: placementModsAction.updateOGPModDetails.type,
      });
    } else if (selectedRowType === 'BillingShiftDifferential') {
      result = yield call(updateBillingShiftDifferentialModDetails, {
        payload: postBillingShiftDifferential(
          placementId,
          formData,
          billingShiftEditRequests,
          selectedButton,
          employeeId,
          modDetails,
        ),
        type: placementModsAction.updateBillingShiftDifferentialModDetails.type,
      });
    } else if (selectedRowType === 'Housing') {
      result = yield call(updateHousingModDetails, {
        payload: postHousing(placementId, formData, selectedButton, employeeId, modDetails),
        type: placementModsAction.updateOGPModDetails.type,
      });
    }

    if (result) {
      yield call(refreshBookingApprovedStatusSpecs);
      yield put(placementModsAction.getPlacementModsList({ placementId: placementId }));
      yield put(placementModsAction.getRefreshPlacementModsList({ placementId: placementId }));
      yield call(refreshModificationGrids, placementId);
    }
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.CommonSAGAError,
        functionName: 'editPlacementModDetails',
        area: 'src/store/redux-store/placement-mods/business-saga.ts',
      },
    });
  }
}

export function* deletePlacementMod(action: PayloadAction<IUpdateModStatusRequest>) {
  try {
    const result = yield call(deleteMods, { ...action, type: placementModsAction.deleteModAction.type });

    if (result) yield call(refreshBookingApprovedStatusSpecs);
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.CommonSAGAError,
        functionName: 'deletePlacementMod',
        area: 'src/store/redux-store/placement-mods/business-saga.ts',
      },
    });
  }
}

export function* batchDeletePlacementMod(action: PayloadAction<IDeleteModStatusRequest>) {
  try {
    const result = yield call(batchDeleteMods, { ...action, type: placementModsAction.deleteModAction.type });

    if (result) yield call(refreshBookingApprovedStatusSpecs);
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.CommonSAGAError,
        functionName: 'batchDeletePlacementMod',
        area: 'src/store/redux-store/placement-mods/business-saga.ts',
      },
    });
  }
}

export function* batchUpdatePlacementMod(action: PayloadAction<any>) {
  const { modifications, placementId } = action.payload;;
    try {
      const result = yield call(batchUpdateMods, {
        payload: { modifications, placementId },
        type: placementModsAction.batchUpdateModDetailsAction.type
      });

      if (result) {
        yield call(refreshBookingApprovedStatusSpecs);
        yield put(placementModsAction.getPlacementModsList({ placementId: placementId }));
        yield put(placementModsAction.getRefreshPlacementModsList({ placementId: placementId }));
        yield call(refreshModificationGrids, placementId);
      }
      
    } catch (error) {
      trackException({
        exception: error,
        properties: {
          name: ExceptionType.CommonSAGAError,
          functionName: 'batchUpdatePlacementMod',
          area: 'src/store/redux-store/placement-mods/business-saga.ts',
        },
      });
    }
}


export function* voidPlacementMod(action: PayloadAction<IUpdateModStatusRequest>) {
  try {
    const result = yield call(voidMods, { ...action, type: placementModsAction.voidModAction.type });

    if (result) yield call(refreshBookingApprovedStatusSpecs);
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.CommonSAGAError,
        functionName: 'voidPlacementMod',
        area: 'src/store/redux-store/placement-mods/business-saga.ts',
      },
    });
  }
}

export function* refreshBookingApprovedStatusSpecs() {
  try {
    const {
      header: { placementId },
      placementOrderType,
    } = yield select(selectPlacementDetails);
    yield all([
      call(requestBookingApprovedRequirements, {
        payload: { placementId, placementOrderType },
        type: placementSummaryActions.getBookingApprovedRequirements.type,
      }),
      call(requestBookingApprovedCheckboxSpecs, {
        payload: { placementId },
        type: placementModsAction.getBookingApprovedCheckboxSpecs.type,
      }),
    ]);
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.CommonSAGAError,
        functionName: 'refreshBookingApprovedStatusSpecs',
        area: 'src/store/redux-store/placement-mods/business-saga.ts',
      },
    });
  }
}
